import { ProfileCardGrid, ProfileGridItem } from '@/components/common/ProfileCardGrid';
import { ProfileHideEmptyContext } from '@/components/common/ProfileFacts';
import { ProfileHeroBadge } from '@/components/common/ProfileHero';
import { ProfileLayout } from '@/components/common/ProfileLayout';
import { STARTUP_SECTION_ORDER } from '@/features/profiles/modules/startup/startup-sections';
import { STARTUP_SECTION_VIEWS } from '@/features/profiles/modules/startup/StartupSectionContent';
import { StartupProfileHero } from '@/features/profiles/modules/startup/StartupProfileHero';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import type { PublicStartupProfile } from '../api/shared-profile.api';
import { sharedCardSpan, toStartupCardData, visibleSharedCards } from '../shared-profile-cards';

const noop = () => undefined;

/**
 * The shared view-only startup profile (MYN-41/58), in the same header and
 * cards as the owner's startup profile (MYN-217, FR-180): read-only, with no
 * Edit buttons, completion % or Export as PDF, and with empty fields and
 * empty cards hidden rather than marked "Not provided". Fed by the redacted
 * public DTO, so it shows only what the share link exposes.
 */
export function SharedProfileContent({ profile }: { profile: PublicStartupProfile }) {
  const data = toStartupCardData(profile);
  const visible = visibleSharedCards(profile);
  const hero = (
    <StartupProfileHero
      data={data}
      readOnly
      canExport={false}
      isExporting={false}
      onExport={noop}
      onOpenSection={noop}
      badges={
        <ProfileHeroBadge tone="muted">
          {UI_STRINGS.SHARED_PROFILE.READ_ONLY_BADGE}
        </ProfileHeroBadge>
      }
    />
  );

  return (
    <ProfileHideEmptyContext.Provider value>
      <ProfileLayout hero={hero}>
        <ProfileCardGrid>
          {STARTUP_SECTION_ORDER.filter((id) => visible[id]).map((id) => (
            <ProfileGridItem key={id} span={sharedCardSpan(id, visible)}>
              {STARTUP_SECTION_VIEWS[id]({ data })}
            </ProfileGridItem>
          ))}
        </ProfileCardGrid>
      </ProfileLayout>
    </ProfileHideEmptyContext.Provider>
  );
}
